<template>
    <div class="mt-5">
        <response-filter-modal :owner-options="owners"></response-filter-modal>
        <response-association-modal></response-association-modal>
        <response-reply-modal></response-reply-modal>
        <div id="responses" :class="[{'border-bottom': collapsed }, 'd-flex justify-content-between align-items-end']">
            <div class="h4">
                <a href="javascript:void(0)" class="mr-2" role="button" @click="collapsed = !collapsed">
                    <i :class="['fas text-muted', collapsed ? 'fa-caret-right' : 'fa-caret-down']"></i>
                </a>
                Received Correspondence
                <span class="badge badge-pill badge-primary unread-badge">{{ unread }} unread</span>          
            </div>                
            <div v-if="!collapsed" class="btn-toolbar" role="toolbar">
                <div class="input-group input-group-sm mr-2">
                    <div class="input-group-prepend">
                        <div class="input-group-text"><i class="fas fa-search fa-sm text-muted"></i></div>
                    </div>
                    <input type="text" class="form-control" placeholder="Search Descriptions" v-model="searchString">
                </div>
                <div class="btn-group"> 
                    <button type="button" class="btn btn-primary btn-sm mb-1" data-toggle="tooltip" title="Filter" @click="showFilters()"><i class="fas fa-filter fa-lg"></i></button>
                    <button type="button" class="btn btn-primary btn-sm mb-1" data-toggle="tooltip" title="Upload Document" @click="uploadDocument()"><i class="fas fa-file-upload fa-lg"></i></button>                      
                </div>
            </div>
        </div>
        <div id="responsesTable" :class="['collapse', { 'show': !collapsed }]">
            <div v-if="isLoading" class="pt-3">            
                <h3 class="text-muted text-center"><i class="fas fa-spinner fa-lg fa-spin align-middle"></i> Loading...</h3>
            </div>
            <div v-else-if="filteredDocuments.length === 0" class="border-top"><h3 class="text-muted text-center mt-3">No Documents.</h3></div>        
            <table v-else class="table table-sm">
                <thead>
                    <tr>
                        <th class="fit"></th>
                        <th class="fit">Document Type</th>
                        <th>Description</th>
                        <th class="fit">Owner</th>
                        <th class="fit">Received</th>
                        <th class="fit"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="document in filteredDocuments" :key="'response-document-' + document.id">
                        <td class="fit">
                            <div class="d-flex flex-row">
                                <div class="pr-1" :title="getStatusText(document)">
                                    <span v-if="document.document_status_id === DocumentStatus.ADDITIONAL_INFORMATION_REQUEST_PENDING || document.document_status_id === DocumentStatus.ADDITIONAL_INFORMATION_RESPONSE_OVERDUE" class="fa-stack fa-sm additional-info-icon">
                                        <i :class="['fas fa-circle fa-stack-1x fa-lg', { 'text-warning':  document.document_status_id === DocumentStatus.ADDITIONAL_INFORMATION_REQUEST_PENDING, 'text-danger': document.document_status_id === DocumentStatus.ADDITIONAL_INFORMATION_RESPONSE_OVERDUE}]"></i>
                                        <i :class="['fas fa-info fa-stack-1x fa-xs', { 'text-dark':  document.document_status_id === DocumentStatus.ADDITIONAL_INFORMATION_REQUEST_PENDING, 'text-light': document.document_status_id === DocumentStatus.ADDITIONAL_INFORMATION_RESPONSE_OVERDUE}]"></i>
                                    </span>
                                    <i v-else :class="getStatusIcon(document)"></i>
                                </div>
                                <div :class="{ 'cursor-pointer': document.read_at }" data-toggle="popover" data-placement="left" :data-content="getReadContent(document)">
                                    <i :class="getIcon(document)"></i>                               
                                </div>
                            </div>                          
                        </td>
                        <td class="fit">
                            <a :href="'/claims/' + claimId + '/documents/' + document.id" :class="{ 'font-weight-bold': !document.read_at }" target="_blank">{{ document.document_type.name }}</a>                             
                        </td>
                        <td>
                            <input v-if="document.edit" class="form-control form-control-sm" v-model="document.description" @blur="changeDescription(document)" @keyup.enter="changeDescription(document)" v-focus>
                            <span v-else @click="document.edit = true;" :title="document.description">                                
                                {{ document.description | truncate(80) }}
                                <a href="javascript:void(0)" class="edit-button"><i class="fas fa-pencil-alt fa-xs"></i></a>
                            </span>                            
                        </td>
                        <td class="fit">{{ document.owner }}</td>
                        <td class="fit">{{ document.created_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                        <td class="fit pl-2">
                            <div class="d-flex flex-nowrap justify-content-end">

                                <div class="flex-fill text-center">
                                    <div v-if="document.document_type_id === DocumentType.LWC_FORM_1010A">
                                        <transmit-button icon="fa-reply" text="Reply" :object="document" @click="clickReply" :emailEnabled="emailEnabled" :disabled="transmissionsDisabled"></transmit-button>
                                    </div>
                                    <div v-else>
                                        <i class="fas fa-fw"></i>
                                    </div>                                                   
                                </div>

                                <div class="flex-fill text-center">
                                    <transmit-button icon="fa-share" text="Forward" :object="document" @click="clickForward" :emailEnabled="emailEnabled" :disabled="transmissionsDisabled"></transmit-button>                                                  
                                </div>                                

                                <!-- associated response with document -->
                                <div class="text-center">
                                    <button class="btn btn-link px-1 py-0" title="Link Response to Document(s)" @click="associateResponse(document)">
                                        <span class="fa-stack fa-xs">
                                            <i class="fas fa-file fa-stack-2x"></i>
                                            <i class="fas fa-link fa-inverse fa-stack-1x"></i>
                                        </span>                                        
                                    </button>                                        
                                </div>

                                <!-- delete document or disassociate document from claim -->
                                <div class="flex-fill text-center">
                                    <button v-if="document.can_delete" class="btn btn-link px-1 py-0" title="Delete Response Document" :disabled="document.is_associated" @click="confirmDelete(document.id)">
                                        <i class="fas fa-trash-alt fa-lg"></i>
                                    </button>                                    
                                    <button v-else-if="document.can_unlink" class="btn btn-link px-1 py-0" title="Unlink from Claim" :disabled="document.is_associated" @click="confirmDisassociate(document.id)">
                                        <i class="fas fa-unlink fa-lg"></i>
                                    </button>
                                </div>                                

                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>        
</template>

<script>
import { DocumentType, DocumentStatus } from '../../constants.js';
import ResponseFilterModal from '../documents/ResponseFilterComponent.vue';
import ResponseAssociationModal from '../documents/ResponseAssociationComponent.vue';
import TransmitButton from '../buttons/TransmitButtonComponent.vue';
import ResponseReplyModal from './ResponseReplyComponent.vue';

export default {
    components: {
        ResponseFilterModal,
        ResponseAssociationModal,
        TransmitButton,
        ResponseReplyModal
    },
    props: {
        claimId: Number,
        emailEnabled: Boolean
    },
    data() {
        return {

            // import constants into the template
            DocumentType: DocumentType,
            DocumentStatus: DocumentStatus,

            documents: [],
            searchString: '',
            filters: {
                readStatus: '',
                associatedStatus: '',
                owner: '',
                received: {
                    begin: null,
                    end: null
                }
            },
            collapsed: false,
            isLoading: false,
            options: [],
            transmissionsDisabled: false
        }
    },
    computed: {
        owners: function () {
            return [...new Set(this.documents.map(e => e.owner))];
        },
        unread: function () {
            return this.documents.filter(e => e.read_by === null).length;
        },
        filteredDocuments: function () {
            const self = this;
            let retval = this.documents;

            // filter on description
            retval = retval.filter(e => (e.description.toLowerCase().indexOf(self.searchString.toLowerCase()) !== -1));

            if (self.filters) {             

                // filter on read status
                if (self.filters.readStatus) {
                    retval = retval.filter(e => {
                        return (self.filters.readStatus.toLowerCase() === 'unread' && e.read_by == null) ||
                            (self.filters.readStatus.toLowerCase() === 'read' && e.read_by != null);
                    });
                }

                // filter on associated status
                if (self.filters.associatedStatus) {
                    retval = retval.filter(e => {
                        return (self.filters.associatedStatus.toLowerCase() === 'unlinked' && !e.is_associated) ||
                            (self.filters.associatedStatus.toLowerCase() === 'linked' && e.is_associated);
                    });
                }

                // filter on owner
                if (self.filters.owner) {
                    retval = retval.filter(e => e.owner.toLowerCase() === self.filters.owner.toLowerCase());
                }

                // filter on received date; we have to convert the date to local time and drop the time
                // portion in order to do the comparison properly
                if (self.filters.received.begin && self.filters.received.end) {
                    retval = retval.filter(e => {
                        let receivedAt = self.getLocalDate(e.received_at);
                        return (receivedAt >= self.filters.received.begin && receivedAt <= self.filters.received.end);
                    });
                }
            }            

            return retval;
        }
    },
    methods: {
        refresh: function (reset = false) {

            // only display loading indicator when page is initially loading
            this.isLoading = reset;

            axios.get('/ajax/claims/' + this.claimId + '/responses')
                .then(response => {

                    this.transmissionsDisabled = response.data.transmissions_disabled;
                    this.documents = response.data.documents.map(e => { this.$set(e, 'edit', false); return e; });

                    if (reset) {
                        this.collapsed = response.data.collapsed;
                        this.options = response.data.options;
                    }                    

                    // enable popovers tooltips
                    $('[data-toggle="tooltip"]').tooltip();
                    $('[data-toggle="popover"]').popover();

                    this.isLoading = false;
                })
                .catch(error => {
                    this.isLoading = false;
                });                
        },
        uploadDocument: function () {

            // default to uploading a form 1010 response
            let selectedOption = DocumentType.LWC_FORM_1010_RESPONSE;

            // show the upload file modal
            eventHub.$emit('show-file-upload', {
                claimId: this.claimId,
                url: '/ajax/claims/' + this.claimId + '/upload',
                options: this.options,
                documentType: selectedOption,
                associateToDocumentId: null
            });
        },
        showFilters: function () {
            eventHub.$emit('show-response-filters', this.filters, this.filter);
        },
        filter: function (filters) {
            this.filters = filters;
        },
        changeDescription: function (document) {

            document.edit = false;

            // update the model
            this.$emit('update');

            // send the change to the server
            axios.patch('/ajax/documents/' + document.id + '/description', {
                'description': document.description,
            })
            .then(response => {
                // nothing to do
            })
            .catch(error => {
                // nothing to do
            });           
        },
        associateResponse: function (document) {
            eventHub.$emit('show-response-association', document);
        },
        getReadContent: function (document) {
            return document.read_by ? 
            'Read by ' + document.read_by.full_name + 
            ' on ' + this.$options.filters.localizeTime(document.read_at, 'YYYY-MM-DD HH:mm:ss') : '';
        },
        markRead: function (document) {

            if (document) {
                let doc = this.documents.find(e => e.id == document.id);
                if (doc) {
                    doc.read_at = document.read_at;
                    doc.read_by = document.read_by;   
                }
            }  
        },
        getLocalDate: function (value) {

            let retval = null;

            if (value) {
                let date = new Date(value);
                retval = new Date(date.getTime() - (date.getTimezoneOffset() * 60 * 1000)).toISOString().split('T')[0];
            }

            return retval;
        },
        confirmDisassociate: function (id) {
            const url = '/ajax/responses/' + id + '/disassociate';
            const message = 'Are you sure you want to unlink this document from the claim?'
            eventHub.$emit('show-disassociate-confirmation', url, null, message);
        },        
        confirmDelete: function (id) {
            eventHub.$emit('show-delete-document-confirmation', id);
        },
        clickForward: function (method, document) {
            eventHub.$emit('show-forward-document', method, document);
        },
        clickReply: function (method, document) {
            eventHub.$emit('show-reply-modal', method, document);
        },
        getIcon: function (document) {
            return document.read_at ? 'far fa-envelope-open fa-lg text-muted' : 'far fa-envelope fa-lg text-muted';      
        },
        getStatusIcon: function (document) {
            let retval = 'fas fa-circle fa-lg icon-hidden';
            if (document.document_status_id === DocumentStatus.GENERIC_DOCUMENT_TRANSMITTING) {
                retval = 'fas fa-spinner fa-spin fa-lg text-muted';
            }
            else if (document.document_status_id === DocumentStatus.GENERIC_DOCUMENT_TRANSMITTED) {
                retval = 'fas fa-paper-plane fa-sm text-muted pb-1';
            }
            else if (document.document_status_id === DocumentStatus.GENERIC_DOCUMENT_TRANSMISSION_FAILED) {
                retval = 'fas fa-exclamation fa-sm text-muted pb-1';
            }
            return retval;
        },
        getStatusText: function (document) {
            return document.document_status ? document.document_status.name : '';
        }                  
    },
    created() {
        this.refresh(true);
        eventHub.$on('update-claim-documents', this.refresh);
        eventHub.$on('update-document-read', this.markRead);
    },
    beforeDestroy() {
        eventHub.$off('update-claim-documents');
        eventHub.$off('update-document-read');
    },    
    directives: {
        focus: {
            inserted: function (el) {
                el.focus();
            }
        }
    }  
}
</script>

<style scoped>
    .edit-button {
        position: relative;
        top: -4px;
        opacity: 0;
    }

    .edit-button:hover {
        opacity: 1;
    }

    .response-icon {
        font-size: 1.0em;
        position: relative;
        top: -2px;
    }

    .unread-badge {
        font-size: .5em;
        position: relative;
        top: -3px;
        /* left: -5px; */
    }

    .icon-hidden {
       color: transparent; 
    }

    .fa-link {
        position:relative;
        top: 2px;
        font-size: .9em;
    }
    
    .fa-unlink {
        font-size: 1em;
    }
    
    .additional-info-icon {
        position: relative;
        top: -2px;
    }

    .input-group {
        align-self: flex-start;
    }      
</style>